import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SphereTrajectoryOption } from '../../commands/practitioner/enums/SphereTrajectoryOption';
import { StroopConfigurationOptions } from '../../commands/practitioner/enums/StroopConfigurationOptions';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import LinearPathkIcon from '../../assets/trajectories/linear-path.svg?react';
import BouncingPathkIcon from '../../assets/trajectories/bouncing-path.svg?react';
import EllipsePathkIcon from '../../assets/trajectories/ellipse-path.svg?react';
import InfinityPathkIcon from '../../assets/trajectories/infinity-path.svg?react';
import SoundTasks from '../../assets/taskSections/IconSound.svg?react';
import EyeTasks from '../../assets/taskSections/EyeMovement.svg?react';
import ReactionTasks from '../../assets/taskSections/IconInteractive.svg?react';
import PlopIcon from '../../assets/sound/IconPlop.svg?react';
import ClicksIcon from '../../assets/sound/IconClick.svg?react';
import BuzzerIcon from '../../assets/sound/IconBuzzer.svg?react';
import CalculationTask from '../../assets/interactive/IconCalculation.svg?react';
import AuditoryTask from '../../assets/interactive/IconHearing.svg?react';
import StroopTask from '../../assets/interactive/IconStroop.svg?react';
import AnimationTask from '../../assets/interactive/IconAnimation.svg?react';
import { ColorSwitchOptions } from '../../commands/practitioner/enums/ColorSwitchOptions';
import {
  PickInteractiveCardGroup,
  pickInteractiveCardOption,
} from './PickInteractiveCardGroup/PickInteractiveCardGroup';
import { PickSoundCardGroup, pickSoundCardOption } from './PickSoundCardGroup/PickSoundCardGroup';
import { SelectionCardGroup, SelectionCardOption } from './SelectionCardGroup/SelectionCardGroup';
import { AccordionBoxComponent } from './AcordionBoxComponent/AccordionBoxComponent';
import { ColourDetails } from './AcordionBoxComponentDetails/ColourDetails/ColourDetails';
import { ColourDetailsLegacy } from './AcordionBoxComponentDetails/ColourDetails/ColourDetailsLegacy';
import { CalculationDetails } from './AcordionBoxComponentDetails/CalculationDetails/CalculationDetails';
import { BuzzerDetails } from './AcordionBoxComponentDetails/BuzzerDetails/BuzzerDetails';
import { SpeedDetails } from './AcordionBoxComponentDetails/SpeedDetails/SpeedDetails';

import { Box } from '@mui/material';
import { ContolPanelTaskTabStyles } from './styles';
import AccordionComponent from '../UI/AccordionExpandIcon/AccordionComponent';
import useCurrentSessionState from '../../hooks/useCurrentSessionState/useCurrentSessionState';
import { commandVersion1_3_0, isVersionLessThan } from '../../utils/versionCheck';

const { Linear, Ellipse, Bouncing, Horizontal8 } = SphereTrajectoryOption;

const NoValue = 5;

const newTrajectOptions: SelectionCardOption[] = [
  { value: Linear, label: 'Linear', Icon: <LinearPathkIcon /> },
  { value: Ellipse, label: 'Ellipse', Icon: <EllipsePathkIcon /> },
  { value: Bouncing, label: 'Bouncing', Icon: <BouncingPathkIcon /> },
  { value: Horizontal8, label: 'Infinity', Icon: <InfinityPathkIcon /> },
  { value: NoValue, label: 'trajectory-off', Icon: null },
];

const interactiveOptions: pickInteractiveCardOption[] = [
  { label: 'visual', Icon: <EyeTasks /> },
  { label: 'auditory', Icon: <AuditoryTask /> },
  { label: 'calculation', Icon: <CalculationTask /> },
  { label: 'stroop', Icon: <StroopTask /> },
  { label: 'animation', Icon: <AnimationTask /> },
];

const soundOptions: pickSoundCardOption[] = [
  { label: 'clicks', Icon: <ClicksIcon />, cardTooltip: 'clicks-tooltip', tooltipPosition: 'left-start' },
  { label: 'plops', Icon: <PlopIcon />, cardTooltip: 'plops-tooltip', tooltipPosition: 'top-end' },
  { label: 'zoomer', Icon: <BuzzerIcon />, cardTooltip: 'zoomer-tooltip', tooltipPosition: 'left-start' },
];

interface ContainerProps {
  sessionUUID: string;
  treatmentUUID: string;
}

const ControlPanelTaskTab: React.FC<ContainerProps> = ({ sessionUUID, treatmentUUID }) => {
  const { t } = useTranslation();
  const {
    eyeMovement,
    speed,
    trajectory,
    interactive,
    reactionAudio,
    reactionVisual,
    calculationDifficulty,
    sound,
    soundClicks,
    soundPlops,
    soundBuzzer,
    setEyeMovement,
    setTrajectory,
    setInteractive,
    setReactionVisual,
    setSpeed,
    setReactionAudio,
    setColorInterval,
    setCalculationDifficulty,
    setSound,
    setSoundClicks,
    setSoundPlops,
    setSoundBuzzer,
    setStroopConfiguration,
    setLastCalculationDifficulty,
    lastCalculationDifficulty,
    setLastBuzzer,
    stroopConfiguration,
    lastBuzzer,
    setReactionAnimation,
    reactionAnimation,
    taskHasStarted,
    game,
  } = usePractitionerControlsContext();
  const [colorOption, setColorOption] = useState(1);
  const { tasksContainer } = ContolPanelTaskTabStyles;
  const { hasDevice } = useAppContext();
  const currentState = useCurrentSessionState() || {};

  const useLegacy = hasDevice && isVersionLessThan(currentState.version ?? '0.0.0', commandVersion1_3_0);

  const targetVersion = commandVersion1_3_0; // '1.3.0'

  const version = currentState.version ?? null;

  const isVersionKnown = version !== null;

  const _isOld = isVersionKnown ? isVersionLessThan(version, targetVersion) : true;

  // keep your public name:
  const isOldVersion = _isOld;

  const filteredInteractiveOptions = React.useMemo(() => {
    // If unknown, be conservative (hide risky features)
    const shouldHide = !isVersionKnown || (hasDevice && isOldVersion);

    if (shouldHide) {
      return interactiveOptions.filter((o) => o.label !== 'stroop' && o.label !== 'animation');
    }
    return interactiveOptions;
  }, [hasDevice, isOldVersion, isVersionKnown]);

  useEffect(() => {
    if (colorOption === 0) {
      setColorInterval(ColorSwitchOptions.Manually);
    } else {
      setColorInterval(ColorSwitchOptions.Automatically);
    }
  });

  const calculationInputHandle = (value: number) => {
    setCalculationDifficulty(value);
    setStroopConfiguration(StroopConfigurationOptions.None);
  };

  return (
    <>
      <Box sx={tasksContainer}>
        <AccordionComponent
          title={'eye-movements'}
          subtitle={'eye-movement-engagement'}
          Icon={<EyeTasks className="icon" />}
          className="eye-movement"
          pwid="pwid-accordion-eye-movement"
        >
          <SelectionCardGroup
            options={newTrajectOptions}
            title={t('trajectory')}
            trajectory={trajectory}
            setTrajectory={setTrajectory}
            setOff={setEyeMovement}
            eyeMovement={eyeMovement}
          />
          <AccordionBoxComponent tooltipPosition="top-end" className="speed" title={t('speed')} tooltip="speed-tooltip">
            <SpeedDetails initialValue={speed * 100} setInputValue={setSpeed} />
          </AccordionBoxComponent>
        </AccordionComponent>
        <AccordionComponent
          title={'interactive'}
          subtitle={'task-engagement'}
          Icon={<ReactionTasks className="icon" />}
          pwid="pwid-accordion-interactive"
        >
          <PickInteractiveCardGroup
            options={filteredInteractiveOptions}
            title={t('reaction-on')}
            calculation={calculationDifficulty}
            setCalculation={setCalculationDifficulty}
            visual={reactionVisual}
            setVisual={setReactionVisual}
            auditory={reactionAudio}
            setAuditory={setReactionAudio}
            lastCalculationDifficulty={lastCalculationDifficulty}
            interactive={interactive}
            setInteractive={setInteractive}
            setStroop={setStroopConfiguration}
            stroop={stroopConfiguration}
            setAnimation={setReactionAnimation}
            animation={reactionAnimation}
          />
          <Box className="interactive-settings">
            <AccordionBoxComponent
              tooltipPosition="top-end"
              tooltip="color-switch-tooltip"
              className="colour"
              title={t('sphere-color')}
            >
              {useLegacy ? (
                <ColourDetailsLegacy
                  autoColour={colorOption}
                  setColorOption={setColorOption}
                  title={t('color-switch')}
                  shouldTriggerEffect={colorOption === 0}
                />
              ) : (
                <ColourDetails
                  autoColour={colorOption}
                  setColorOption={setColorOption}
                  shouldTriggerEffect={colorOption === 0}
                  title={t('color-switch')}
                  taskHasStarted={taskHasStarted}
                />
              )}
            </AccordionBoxComponent>
            <AccordionBoxComponent
              tooltipPosition="left-start"
              tooltip="calculation-task-tooltip"
              className="calculation"
              title={t(`calculation-task`)}
            >
              <CalculationDetails
                calculation={calculationDifficulty}
                value={calculationDifficulty}
                setValue={calculationInputHandle}
                setLastCalculationDifficulty={setLastCalculationDifficulty}
              />
            </AccordionBoxComponent>
          </Box>
        </AccordionComponent>
        <AccordionComponent
          title={'sound'}
          subtitle={'sound-engagement'}
          Icon={<SoundTasks className="icon" />}
          pwid="pwid-accordion-sound"
        >
          <PickSoundCardGroup
            options={soundOptions}
            title={t('present')}
            clicks={soundClicks}
            plops={soundPlops}
            lastBuzzer={lastBuzzer}
            buzzer={soundBuzzer}
            setClicks={setSoundClicks}
            setPlops={setSoundPlops}
            setBuzzer={setSoundBuzzer}
            setSound={setSound}
            sound={sound}
          />
          <AccordionBoxComponent className="buzzer" title={t('task-settings')}>
            <BuzzerDetails setBuzzer={setLastBuzzer} setSoundBuzzer={setSoundBuzzer} soundBuzzer={soundBuzzer} />
          </AccordionBoxComponent>
        </AccordionComponent>
      </Box>
    </>
  );
};

export default ControlPanelTaskTab;
